// TODO: move module out so we can use in allevaApp
/*jshint esversion: 6 */

import angular from 'angular';
import Noty from 'noty';

// css
import 'noty/lib/noty.css';
import 'noty/lib/themes/bootstrap-v3.css';

const apiServerError         = '<h5>Server Error</h5> <p>Please try again later. If the error persists, please contact our support team for assistance.</p>';
const apiBadRequestError     = '<h5>Bad Request Error</h5> <p>Please try again later. If the error persists, please contact our support team for assistance.</p>';
const apiAuthenticationError = "<h5>Session Expired</h5> <p>Please sign in. If this error persists, please contact our support team for assistance.</p>";

function AllevaApi($http, noty) {
    const serviceBase = ALLEVA_API_HOST;   // injected by webpack
    const authBase    = `${ALLEVA_AUTH_HOST}/auth`;

    // function to wrap gets so we handle everything consistently
    const httpGet = function (url, params, secure = true) {

        var req = {
            method: 'GET',
            url   : url,
            params: params,
            secure: secure
        };

        return $http(req)
         .then(function(response) {
            return response.data;
        }).catch(function(e) {
            allevaError(e);
        }).finally(function() {
            // do work
        });

    };

    const httpPost = function (url, params, secure = true) {

        var req = {
            method : 'POST',
            url    : url,
            headers: {
              'Content-Type': "application/json"
            },
            data  : params,
            secure: secure
        };

        return $http(req)
         .then(function(response) {
            return response.data;
        }).catch(function(e) {
            allevaError(e);
        }).finally(function() {
            // do work
        });
    };

    const httpPut = function (url, params, secure = true) {

        var req = {
            method : 'PUT',
            url    : url,
            headers: {
              'Content-Type': "application/json"
            },
            data  : params,
            secure: secure
        };

        return $http(req)
         .then(function(response) {
            return response.data;
        }).catch(function(e) {
            allevaError(e);
        }).finally(function() {
            // do work
        });
    };

    const httpDelete = function (url, params, secure = true) {
        
        const req = {
            method: 'DELETE',
            url: url,
            headers: {
                'Content-Type': 'application/json',
            },
            data: JSON.stringify(params || {}),
            secure: secure
        };

        return $http(req)
            .then((response) => response.data)
            .catch((e) => allevaError(e))
            .finally(() => { });
    };

    const allevaError = (e) => {
        // Skip default error notification if suppressDefaultError flag is set
        if (!e.suppressDefaultError) {
            // Helper function to format error messages consistently
            const showErrorNotification = (message) => {
                const formattedMessage = typeof message === 'string' && message.trim().startsWith('<h5>') 
                    ? message 
                    : `<h5>Error</h5> <p>${message}</p>`;
                new noty({ text: formattedMessage, type: 'error' }).show();
            };

            // Handle errors based on status code
            switch(e.status) {
                case 400:   // Bad request
                    showErrorNotification(apiBadRequestError);
                    break;
                case 401:   // Unauthorized
                    showErrorNotification(apiAuthenticationError);
                    break;
                case 403:   // Forbidden
                    if(e.data.errors){
                        e.data.errors.forEach(error => {
                            showErrorNotification(error);
                        });
                    }
                    else if (typeof(e.data) === 'string' || e.data instanceof String) {
                        showErrorNotification(e.data);
                    }
                    break;
                case 408:   // Gateway Timeout
                    showErrorNotification(e.data || apiServerError);
                    break;
                case 409:   // Conflict
                    showErrorNotification(e.data || apiServerError);
                    break;
                case 417:   // Expectation Failed
                    showErrorNotification(e.data || apiServerError);
                    break;
                case 429:   // Too Many Requests
                    showErrorNotification(e.data || apiServerError);
                    break;
                case 435:   // Error handling for Nexio Transaction
                    showErrorNotification(e.data || apiServerError);
                    break;
                case 500:   // Server error
                    showErrorNotification(apiServerError);
                    break;
                default:
                    showErrorNotification(apiServerError);
            }
        }

        throw e;
    };

    // setup noty defaults
    noty.overrideDefaults({
        type   : 'error',
        theme  : 'bootstrap-v3',
        timeout: 5000
    });

    return {
        Admin: {
            ExternalService: {
                Provider: {
                    get(filters) {
                        const queryString = '?' + new URLSearchParams(filters);
                        return httpGet(`${serviceBase}/v2/admin/external-service/providers${queryString}`, {}, true);
                    }
                }
            }
        },
        Auth: {
            login(filters) {
                return httpPost(`${authBase}/login/`, filters);
            },
            signup(filters) {
                return httpPost(`${authBase}/signup/`, filters);
            },
            forgotUsername(filters){
                return httpPost(`${authBase}/forgot-username/`, filters);
            },
            forgotPassword(filters){
                return httpPost(`${authBase}/forgot-password/`, filters);
            },
            resetPassword(filters){
                return httpPost(`${authBase}/reset-password/`, filters);
            },
            getAllRoles(filters){
                return httpGet(`${authBase}/roles`, filters, false);
            }
        },
        ClientMedications: {
            get(leadId, params) {
              const queryString = `?${new URLSearchParams(params)}`;
              return httpGet(`${serviceBase}/v2/clients/${leadId}/medications${queryString}`, {}, true);
            },
        },
        FamilyAPI: {
            health(filters) {
                return httpGet(`${serviceBase}/health`, filters, false);
            },

            createRehabUser(filters) {
                return httpPost(`${serviceBase}/family/create-user`, filters);
            },
            signUp(filters) { // Un-authenticated call
                return httpPost(`${serviceBase}/family/sign-ups`, filters, false);
            },
            currentUserDetails(params) {
                const queryString = '?' + new URLSearchParams(params);
                return httpGet(`${serviceBase}/family/user-for-portal` + queryString, {});
            },
            getParticipantChecklist(filters){
                return httpGet(`${serviceBase}/family/participant-checklist`, filters)
            },
            getParticipantChecklistBadgeCount(filters){
                return httpGet(`${serviceBase}/family/participant-checklist-badge`, filters)
            },
            getForms(filters){
                return httpGet(`${serviceBase}/family/checklist`, filters);
            },
            getClients(params) {
                const queryString = '?' + new URLSearchParams(params);
                return httpGet(`${serviceBase}/family/clients` + queryString, {});
            },
            createProspect(filters) {
                return httpPost(`${serviceBase}/family/create-prospect`, filters);
            },
            getStyles(filters) {
                const queryString = '?' + new URLSearchParams(filters);
                return httpPost(`${serviceBase}/family/styles` + queryString, {});
            },
            getClientUpcomingSessions(filters) {
                return httpGet(`${serviceBase}/family/upcoming-sessions`, filters);
            },
            getClientImagePath(fileName) {
                return `${serviceBase}/v2/images/clients/` + fileName;
            }
        },
        CommonAPI: {
            rehabDetails(params) { // Un-authenticated call
                const queryString = '?' + new URLSearchParams(params);
                return httpGet(`${serviceBase}/common/rehab-details` + queryString, {}, false);
            },
            countries() {
                return httpGet(`${serviceBase}/common/countries`, {});
            },
            states(params) {
                const queryString = '?' + new URLSearchParams(params);
                return httpGet(`${serviceBase}/common/states` + queryString, {});
            },
            relationships() {
                return httpGet(`${serviceBase}/common/relationships`, {});
            },
        },
        Forms: {
            submitForm(filters) {
                return httpPost(`${serviceBase}/family/submit-form`, filters);
            }
        },
        AdvancedForms: {
            submitForm(formId, leadId, dataValues) {
                return httpPost(`${serviceBase}/advanced-forms/${formId}/data/${leadId}`, {'DataValues': dataValues});
            },
            getAdvancedForm(advancedFormId, instanceCreatedDate){
                return httpGet(`${serviceBase}/advanced-forms/${advancedFormId}/Published`, {'time': instanceCreatedDate})
            },
            saveAdvancedFormHtnl(data){
                return httpPost(`${serviceBase}/advanced-form/record`, data)
            },
            AdvancedFormElements: {
                getElementsForFormId(formId){
                    return httpGet(`${serviceBase}/advanced-form-elements?advancedFormId=${formId}`)
                }
            },
            DataBackup:{
                add(params) {
                    return httpPost(`${serviceBase}/advanced-forms/data-backup`, params)
                }
            },
            DataFacets:{
                search(params){
                    return httpPost(`${serviceBase}/data-facets/search`, params)
                }
            },
            DataValues:{
                search(params) {
                    return httpPost(`${serviceBase}/data-values/search`, params)
                },

                save(id, value){
                    return httpPut(`${serviceBase}/data-values/${id}`, value)
                }
            },
            FormInstance:{
                get(params) {
                    const queryString = `?${new URLSearchParams(params)}`;
                    return httpGet(`${serviceBase}/advanced-forms/instance${queryString}`, {}, true);
                },
                add(instance) {
                    return httpPost(`${serviceBase}/advanced-forms/instance`, instance, true);
                }
            }
        },
        TreatmentPlan: {
            submitSignature(filters) {
                return httpPost(`${serviceBase}/treatment-plans/` + filters.tpid + `/signature`, filters);
            },
            submitReviewSignature(filters) {
                return httpPost(`${serviceBase}/treatment-plans/` + filters.tprid + `/review-signature`, filters);
            }
        },
        Messages: {
            getPortalEmails(filters){
                return httpGet(`${serviceBase}/family/portal-emails`, filters);
            },
            getEmailMessage(filters){
                return httpGet(`${serviceBase}/family/email-message`, filters);
            },
            readEmail(params) {
                const queryString = '?' + new URLSearchParams(params);
                return httpPost(`${serviceBase}/family/read-email` + queryString, {});
            },
            sendEmail(filters) {
                return httpPost(`${serviceBase}/family/send-message-to-emr`, filters);
            },
            getAttachmentPath(fileName) {
                return `${serviceBase}/v2/documents/attachments/` + fileName;
            }
        },
        Classrooms: {
            isAuthenticated() {
                return httpGet(`${serviceBase}/classrooms/is-authenticated`, {}, true);
            },
            getClientCourses(userId) {
                const queryString = '?' + new URLSearchParams(userId);
                return httpGet(`${serviceBase}/classrooms/client-courses` + queryString, {}, true);
            },
            inviteGuardian(invite) {
                return httpPost(`${serviceBase}/classrooms/invite-guardian`, invite, true);
            },
        },
        Clients: {
            getDoc(leadId) {
                return httpGet(`${serviceBase}/clients/` + leadId + `/documents`, {}, true);
            },
            saveDoc(leadId, params) {
                return httpPost(`${serviceBase}/clients/` + leadId + `/documents`, params, true);
            },
            deleteDoc(leadId, docId) {
                return httpDelete(`${serviceBase}/clients/` + leadId + `/documents/` + docId, {}, true);
            },
        },
        MasterCodes: {
            search(params) {
                return httpPost(`${serviceBase}/master-codes/search`, params);
            }
        },
        Nexio: {
            setting:{
                get(domain) {
                    return httpGet(`${serviceBase}/v2/admin/${domain}/settings/nexio`, {}, true);
                },
            },
            Card:{
                get(userId) {
                    return httpGet(`${serviceBase}/v2/family/${userId}/payments/ext_nexio/cards`, {}, true);
                },
                create(userId, cardData) {
                    return httpPost(`${serviceBase}/v2/family/${userId}/payments/ext_nexio/cards`, cardData, true);
                },
                getIFrame(userId, facilityId) {
                    const queryString = facilityId != null ? `?${new URLSearchParams({ facilityId })}` : '';
                    return httpGet(`${serviceBase}/v2/family/${userId}/payments/ext_nexio/cards/iframe${queryString}`, {}, true);
                },
                delete(userId, cardId) {
                    return httpDelete(`${serviceBase}/v2/family/${userId}/payments/ext_nexio/cards/${cardId}`, {}, true);
                },
                runTransaction(userId, id, params, transactionData) {
                    const queryString = params != null ? `?${new URLSearchParams(params)}` : '';
                    return httpPost(`${serviceBase}/v2/family/${userId}/payments/ext_nexio/cards/${id}${queryString}`, transactionData, true);
                }
            },
            Transaction:{
                get(leadId, params) {
                    const queryString = `?${new URLSearchParams(params)}`;
                    return httpGet(`${serviceBase}/v2/clients/${leadId}/payments/ext_nexio/transactions${queryString}`, {}, true);
                }
            },
        },
    };
}

export default angular.module('alleva.api', [])
    .service('allevaApi', ['$http', 'noty', AllevaApi])
    .config(['$httpProvider', function ($httpProvider) {
        // send the cookie from allevasoft with every request to authenticate
        //$httpProvider.defaults.withCredentials = true;
    }])
    .constant('noty', Noty)
    .name;
